import { cn } from '@/lib/cn';
import { faCamera } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Presence } from 'components/Presence';
import DropdownMenu from 'components/dropdownMenu';
import Text from 'components/primitives/components/Text';
import { checkFileTypeIsImage, imageExtensions, readFileAsDataURL, workspaceAvatarMaxSizeInBytes } from 'lib/blobs';
import { useRef, useState, type FC } from 'react';
import { Controller } from 'react-hook-form';

export const PluginIconPicker: FC = () => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    return (
        <Controller
            name='icon'
            render={(props) => (
                <>
                    <div
                        className={cn(
                            'group h-[9.15rem] w-[9.15rem] relative rounded-input overflow-hidden bg-transparent border-4 border-componentBackgroundPrimary ring-1 ring-outlinePrimary mx-auto flex items-center justify-center',
                            props.field.value && 'bg-white'
                        )}
                    >
                        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                            <DropdownMenu.Button
                                className='absolute inset-0 z-10 opacity-0 peer'
                                aria-label='open file picker'
                            />
                            <DropdownMenu.Menu className='max-w-[22rem] mt-2 translate-x-1' align='end'>
                                <DropdownMenu.Item
                                    onSelect={() => {
                                        if (!inputRef.current) {
                                            throw new Error('file uploader is missing from the dom');
                                        }
                                        inputRef.current.value = '';
                                        inputRef.current.click();
                                    }}
                                >
                                    Upload image
                                </DropdownMenu.Item>
                                <Presence isPresent={props.field.value}>
                                    <DropdownMenu.Item
                                        className='text-textDestructive'
                                        onSelect={() => props.field.onChange(null)}
                                    >
                                        Delete image
                                    </DropdownMenu.Item>
                                </Presence>
                                <DropdownMenu.Separator />
                                <Text.Body className='px-3 py-2 text-textSecondary'>
                                    Hint: use a square image for the best display results.
                                </Text.Body>
                            </DropdownMenu.Menu>
                        </DropdownMenu>

                        {props.field.value ? (
                            <img
                                className='absolute inset-0 object-contain w-full h-full'
                                alt='Plugin Icon'
                                src={props.field.value}
                            />
                        ) : (
                            <div className='absolute inset-0 flex items-center justify-center text-4xl pointer-events-none text-textSecondary after:absolute after:inset-0 after:bg-backgroundSecondary after:opacity-80'>
                                <FontAwesomeIcon icon={faCamera} className='z-10' />
                            </div>
                        )}
                    </div>

                    <Presence isPresent={Boolean(errorMsg)}>
                        <p className='text-center text-textDestructive first-letter:capitalize'>{errorMsg}</p>
                    </Presence>

                    <input
                        ref={inputRef}
                        className='hidden'
                        type='file'
                        accept={imageExtensions.join(',')}
                        onChange={async (e) => {
                            try {
                                setErrorMsg(null);
                                const pluginIcon = e.target.files?.item(0);
                                if (!pluginIcon) {
                                    return;
                                }

                                if (!checkFileTypeIsImage(pluginIcon)) {
                                    throw new Error('The file uploaded was not compatible (PNG, JPG, SVG, GIF, WEBP).');
                                }

                                if (pluginIcon.size > workspaceAvatarMaxSizeInBytes) {
                                    throw new Error(`File cannot exceed ${workspaceAvatarMaxSizeInBytes / 1024} KB`);
                                }

                                const iconUrl = await readFileAsDataURL(pluginIcon);

                                props.field.onChange(iconUrl);
                            } catch (error) {
                                if (error instanceof Error) {
                                    setErrorMsg(error.message);
                                }
                            }
                        }}
                    />
                </>
            )}
        />
    );
};
